import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiX, FiDroplet } from 'react-icons/fi';
import { apiService } from '@/utils/api';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import WaveCircle from '@/components/calibration/WaveCircle';
import SmileIconSvg from '@/components/calibration/SmileIconSvg';

// pH Calibration Actions (mirror Android CalibrationConstants)
const PhActions = {
  PH_CHECK_EVT: 0,
  PH__MODE_ON: 1,
  PH__START_CALIBRATION_PH7: 2,
  PH__START_CALIBRATION_PH10: 3,
  PH__MONITOR_PH7: 4,
  PH__MONITOR_PH10: 5,
  PH__STOP_CALIBRATION_PH7: 6,
  PH__STOP_CALIBRATION_PH10: 7,
  PH__STORE_VALUE_PH7: 8,
  PH__STORE_VALUE_PH10: 9,
  PH__STORE_VALUE_PH7_AND_PH10: 10,
  PH__MODE_OFF: 11,
} as const;

// Event badge class mapping for quick status visualization
function statusBadgeClass(status: string) {
  const s = status.toUpperCase();
  if (s.includes('READY')) return 'px-2 py-1 text-xs rounded-md bg-green-100 text-green-700';
  if (s.includes('RUNNING')) return 'px-2 py-1 text-xs rounded-md bg-blue-100 text-blue-700';
  if (s.includes('STABLE')) return 'px-2 py-1 text-xs rounded-md bg-teal-100 text-teal-700';
  if (s.includes('OUT OF RANGE')) return 'px-2 py-1 text-xs rounded-md bg-red-100 text-red-700';
  if (s.includes('STOPPED')) return 'px-2 py-1 text-xs rounded-md bg-gray-200 text-gray-700';
  if (s.includes('VALUE ACCEPTED')) return 'px-2 py-1 text-xs rounded-md bg-green-200 text-green-800';
  return 'px-2 py-1 text-xs rounded-md bg-gray-100 text-gray-700';
}

function parsePhCalibrationResponse(payload: any): { eventText: string; eventCode: number | null; value: number | null } {
  const root = (payload?.data?.result) ?? (payload?.data?.message) ?? (payload?.result) ?? (payload?.message) ?? payload;
  if (!root || typeof root !== 'object') return { eventText: '-', eventCode: null, value: null };
  let eventText: string = root?.event_text ?? root?.status_text ?? root?.status ?? '-';
  let eventCode: any = root?.event_code ?? root?.event ?? null;
  let value: any = root?.value ?? null;
  // Normalize value to float
  if (typeof value === 'string') {
    const n = Number(value);
    value = isNaN(n) ? null : n;
  }
  if (typeof eventCode === 'string') {
    const n = Number(eventCode);
    eventCode = isNaN(n) ? null : n;
  }
  return { eventText, eventCode: eventCode ?? null, value: value ?? null };
}

type Props = {
  deviceId: string;
  onClose: () => void;
};

const PhCalibrationSidebar: React.FC<Props> = ({ deviceId, onClose }) => {
  const [phase, setPhase] = useState<'Intro' | 'Loading' | 'Running' | 'Stable' | 'StoredPH7' | 'StoredPH10' | 'Stopped' | 'Done' | 'Failed'>('Intro');
  const [eventText, setEventText] = useState<string>('-');
  const [eventCode, setEventCode] = useState<number | null>(null);
  const [currentValue, setCurrentValue] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [activeStep, setActiveStep] = useState<1 | 2>(1); // 1 => PH7, 2 => PH10
  const pollTimer = useRef<any>(null);
  const mountedRef = useRef<boolean>(false);
  const monitorDelayMs = 500;

  // Confirm dialog state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState('');
  const [confirmOkText, setConfirmOkText] = useState('Confirm');
  const [confirmCancelText, setConfirmCancelText] = useState('Cancel');
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmContent, setConfirmContent] = useState<React.ReactNode>('');
  const confirmActionRef = useRef<null | (() => Promise<void>)>(null);

  const stepLabel = useMemo(() => (activeStep === 1 ? 'pH 7.0' : 'pH 10.0'), [activeStep]);

  useEffect(() => {
    mountedRef.current = true;
    // Initial check
    sendPhCalibrationRequest(PhActions.PH__MODE_ON);
    // After entering calibration mode, start monitoring PH7 after a short delay
    if (pollTimer.current) {
      clearTimeout(pollTimer.current);
      pollTimer.current = null;
    }
    pollTimer.current = setTimeout(() => {
      setActiveStep(1);
      sendPhCalibrationRequest(PhActions.PH__MONITOR_PH7);
    }, monitorDelayMs);
    return () => {
      mountedRef.current = false;
      if (pollTimer.current) {
        clearTimeout(pollTimer.current);
        pollTimer.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  function stopPolling() {
    if (pollTimer.current) {
      clearTimeout(pollTimer.current);
      pollTimer.current = null;
    }
  }

  async function sendPhCalibrationRequest(action: number) {
    if (!deviceId) return;
    setLoading(true);
    try {
      const res = await apiService.post<any>(`/api/v1/iot-shadow/device/${deviceId}/calibratePh`, { action });
      const parsed = parsePhCalibrationResponse(res ?? {});
      setEventText(parsed.eventText);
      setEventCode(parsed.eventCode);
      setCurrentValue(parsed.value);

      const txt = parsed.eventText.toUpperCase();
      if (txt.includes('RUNNING PH7') || txt.includes('RUNNING PH10')) {
        setPhase('Running');
        // Keep monitoring according to step
        const monitorAction = activeStep === 1 ? PhActions.PH__MONITOR_PH7 : PhActions.PH__MONITOR_PH10;
        pollTimer.current = setTimeout(() => sendPhCalibrationRequest(monitorAction), 2500);
      } else if (txt.includes('READING STABLE PH7')) {
        setPhase('Stable');
        setActiveStep(1);
      } else if (txt.includes('READING STABLE PH10')) {
        setPhase('Stable');
        setActiveStep(2);
      } else if (txt.includes('VALUE ACCEPTED PH7')) {
        setPhase('StoredPH7');
        // After PH7 stored, show success UI with options below; do not auto-progress
      } else if (txt.includes('VALUE ACCEPTED PH10')) {
        // Show success UI for PH10; do not auto-finish
        setPhase('StoredPH10');
      } else if (txt.includes('STOPPED')) {
        setPhase('Stopped');
      } else if (txt.includes('OUT OF RANGE')) {
        setPhase('Failed');
      } else if (txt.includes('READY')) {
        // Treat READY as a stable reading state per Android flow
        setPhase('Stable');
      }
    } catch (e) {
      console.error('pH calibration request failed:', e);
      // If we were running, try to continue monitoring briefly
      if (phase === 'Running') {
        const monitorAction = activeStep === 1 ? PhActions.PH__MONITOR_PH7 : PhActions.PH__MONITOR_PH10;
        pollTimer.current = setTimeout(() => sendPhCalibrationRequest(monitorAction), 3000);
      }
    } finally {
      setLoading(false);
    }
  }

  async function onPrimaryCtaClick() {
    const label = primaryCtaLabel();
    if (label === 'Start pH 7') {
      await sendPhCalibrationRequest(PhActions.PH__MODE_ON);
      await sendPhCalibrationRequest(PhActions.PH__START_CALIBRATION_PH7);
      stopPolling();
      pollTimer.current = setTimeout(() => sendPhCalibrationRequest(PhActions.PH__MONITOR_PH7), monitorDelayMs);
    } else if (label === 'Store pH 7') {
      // Stop polling and confirm storing PH 7.0 using ConfirmDialog
      stopPolling();
      setConfirmTitle('Save pH 7.0');
      setConfirmContent(<p className="text-sm">Confirm saving the pH 7.0 calibration value?</p>);
      setConfirmOkText('Save');
      setConfirmCancelText('Cancel');
      confirmActionRef.current = async () => {
        await sendPhCalibrationRequest(PhActions.PH__STORE_VALUE_PH7);
      };
      setConfirmOpen(true);
    } else if (label === 'Start pH 10') {
      await sendPhCalibrationRequest(PhActions.PH__START_CALIBRATION_PH10);
      stopPolling();
      pollTimer.current = setTimeout(() => sendPhCalibrationRequest(PhActions.PH__MONITOR_PH10), monitorDelayMs);
    } else if (label === 'Store pH 10') {
      // Confirm storing PH10 value
      stopPolling();
      setConfirmTitle('Save pH 10.0');
      setConfirmContent(<p className="text-sm">Confirm saving the pH 10.0 calibration value?</p>);
      setConfirmOkText('Save');
      setConfirmCancelText('Cancel');
      confirmActionRef.current = async () => {
        await sendPhCalibrationRequest(PhActions.PH__STORE_VALUE_PH10);
      };
      setConfirmOpen(true);
    } else if (label === 'Finish') {
      // Directly finish and close without confirmation
      await sendPhCalibrationRequest(PhActions.PH__STORE_VALUE_PH7_AND_PH10);
      stopPolling();
      onClose();
    } else if (label === 'Done') {
      onClose();
    }
  }

  async function onSecondaryCtaClick() {
    if (activeStep === 1) {
      await sendPhCalibrationRequest(PhActions.PH__STOP_CALIBRATION_PH7);
    } else {
      await sendPhCalibrationRequest(PhActions.PH__STOP_CALIBRATION_PH10);
    }
    setPhase('Stopped');
  }

  function primaryCtaLabel(): string {
    if (phase === 'Running') return 'Stop';
    if (phase === 'Stable') return activeStep === 1 ? 'Store pH 7' : 'Store pH 10';
    if (phase === 'StoredPH7') return 'Finish';
    if (phase === 'StoredPH10') return 'Finish';
    if (phase === 'Done') return 'Done';
    if (phase === 'Stopped') return 'Start pH 7';
    if (phase === 'Failed') return activeStep === 1 ? 'Store pH 7' : 'Store pH 10';
    return 'Start pH 7';
  }

  const canStop = useMemo(() => phase === 'Running' || phase === 'Stable', [phase]);
  const showToggle = useMemo(() => phase === 'Intro' || phase === 'StoredPH7', [phase]);

  return (
    <div className="fixed inset-0 z-40">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Sidebar */}
      <div className="absolute right-0 top-0 h-full w-full sm:w-[480px] bg-white dark:bg-gray-900 shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center gap-2">
            <FiDroplet className="text-primary-500" />
            <div>
              <div className="text-base font-semibold">pH Calibration</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2">
                <span>{stepLabel}</span>
                <span>•</span>
                <span className={statusBadgeClass(eventText)}>{eventText}</span>
              </div>
            </div>
          </div>
          <button className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800" onClick={onClose} aria-label="Close">
            <FiX className="text-lg" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto px-4 py-4">
          {/* Intro step removed: start in calibration mode and show live readings */}

          {phase === 'Loading' && (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="animate-spin h-10 w-10 rounded-full border-4 border-gray-300 border-t-primary-600" />
              <div className="mt-3 text-sm text-gray-700 dark:text-gray-300">Starting pH 10 calibration…</div>
            </div>
          )}

          {(phase === 'Running' || phase === 'Stable' || phase === 'Failed') && (
            <div className="space-y-3">
              <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-4 flex flex-col items-center">
                {/* Animated WaveCircle gauge */}
                <WaveCircle
                  valueDeci={currentValue}
                  size={144}
                  phase={phase === 'Stable' ? 'Stable' : phase === 'Failed' ? 'Failed' : 'Running'}
                  label={activeStep === 1 ? 'pH 7.0' : 'pH 10.0'}
                />
                <div className="mt-4 w-full">
                  {/* Progress bar */}
                  <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-3 ${phase === 'Stable' ? 'bg-teal-500' : phase === 'Failed' ? 'bg-red-500' : 'bg-primary-600'}`}
                      style={{ width: `${Math.min(100, Math.max(0, (currentValue ?? 0) / (activeStep === 1 ? 14 : 14) * 100))}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-gray-600 dark:text-gray-300">Current</span>
                    <span className="text-xs font-medium">{currentValue != null ? (currentValue / 10).toFixed(2) : '-'}</span>
                  </div>
                  <div className="mt-2 text-xs text-gray-700 dark:text-gray-300 text-center">
                    {phase === 'Stable' ? 'Once value has stabilised tap on Save' : eventText}
                  </div>
                </div>
              </div>

              <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-3">
                <div className="flex items-center justify-between">
                  <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">{stepLabel}</div>
                  <span className={statusBadgeClass(eventText)}>{eventText}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Target</span>
                    <span className="text-sm font-medium">{activeStep === 1 ? '7.00' : '10.00'}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {phase === 'StoredPH7' && (
            <div className="space-y-4">
              <div className="rounded-lg bg-white dark:bg-gray-800 p-6 border border-gray-200 dark:border-gray-700 flex flex-col items-center">
                <SmileIconSvg size={200} />
                <div className="mt-3 text-base font-semibold text-gray-900 dark:text-gray-100">pH 7.0 value stored</div>
                <div className="mt-1 text-sm text-gray-600 dark:text-gray-300 text-center" style={{ whiteSpace: 'pre-line' }}>You have finished one point
calibration!</div>
              </div>
            </div>
          )}

          {phase === 'StoredPH10' && (
            <div className="space-y-4">
              <div className="rounded-lg bg-white dark:bg-gray-800 p-6 border border-gray-200 dark:border-gray-700 flex flex-col items-center">
                <SmileIconSvg size={200} />
                <div className="mt-3 text-base font-semibold text-gray-900 dark:text-gray-100">pH 10.0 value stored</div>
                <div className="mt-1 text-sm text-gray-600 dark:text-gray-300 text-center" style={{ whiteSpace: 'pre-line' }}>You have finished two point 
calibration!</div>
              </div>
            </div>
          )}

          {(phase === 'Stopped' || phase === 'Done') && (
            <div className="space-y-3">
              <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-3">
                <div className="flex items-center justify-between">
                  <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">{stepLabel}</div>
                  <span className={statusBadgeClass(eventText)}>{eventText}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Target</span>
                    <span className="text-sm font-medium">{activeStep === 1 ? '7.00' : '10.00'}</span>
                  </div>
                </div>
              </div>
              {phase === 'Stopped' && (
                <div className="px-3 py-2 rounded-md bg-gray-100 text-gray-700 text-sm">Calibration stopped.</div>
              )}
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-800 flex items-center justify-between">
          <div className="text-xs text-gray-500 dark:text-gray-400">{loading ? 'Processing…' : ' '}</div>
          {phase === 'StoredPH7' ? (
            <div className="flex items-center gap-2">
              <button
                className="px-3 py-2 rounded-md text-sm bg-gray-200 text-gray-700 hover:bg-gray-300"
                onClick={async () => {
                  await sendPhCalibrationRequest(PhActions.PH__STORE_VALUE_PH7_AND_PH10);
                  stopPolling();
                  onClose();
                }}
              >
                Finish
              </button>
              <button
                className="px-3 py-2 rounded-md text-sm bg-primary-600 text-white hover:bg-primary-700"
                onClick={async () => {
                  // Continue to PH10
                  setActiveStep(2);
                  setPhase('Loading');
                  await sendPhCalibrationRequest(PhActions.PH__START_CALIBRATION_PH10);
                  stopPolling();
                  pollTimer.current = setTimeout(() => sendPhCalibrationRequest(PhActions.PH__MONITOR_PH10), monitorDelayMs);
                }}
              >
                Continue pH 10
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              {phase === 'Loading' || loading ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin h-5 w-5 rounded-full border-2 border-gray-300 border-t-primary-600" />
                  <span className="text-sm text-gray-600 dark:text-gray-300">Loading…</span>
                </div>
              ) : (
                <>
                  {canStop && (
                    <button
                      className="px-3 py-2 rounded-md text-sm bg-gray-200 text-gray-700 hover:bg-gray-300"
                      onClick={onSecondaryCtaClick}
                    >
                      Stop
                    </button>
                  )}
                  <button
                    className={`px-3 py-2 rounded-md text-sm bg-primary-600 text-white hover:bg-primary-700`}
                    onClick={onPrimaryCtaClick}
                  >
                    {primaryCtaLabel()}
                  </button>
                </>
              )}
            </div>
          )}
      </div>
      </div>

      {/* ConfirmDialog for save/finish actions */}
      <ConfirmDialog
        open={confirmOpen}
        title={confirmTitle}
        onCancel={() => setConfirmOpen(false)}
        onOk={async () => {
          if (!confirmActionRef.current) {
            setConfirmOpen(false)
            return
          }
          try {
            setConfirmLoading(true)
            await confirmActionRef.current()
          } finally {
            setConfirmLoading(false)
            setConfirmOpen(false)
          }
        }}
        cancelText={confirmCancelText}
        okText={confirmOkText}
        loading={confirmLoading}
        fullWidth
        maxWidth="sm"
      >
        {confirmContent}
      </ConfirmDialog>
    </div>
  );
};

export default PhCalibrationSidebar;